import { Loader2 } from "lucide-react";
import { useState } from "react";

import {
  type CarrierAnalyticsCountRow,
  type CarrierOperatorEfficiencyRow,
  useCarrierAlerts,
  useCarrierAnalytics,
  useCarrierOperatorEfficiency,
  useCarrierScorecard,
} from "@/api/carrier";
import { ColumnsMenu } from "@/components/data-table";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDateOnly } from "@/lib/format";
import { cn } from "@/lib/utils";

const TOGGLEABLE_COLUMNS = [
  { id: "dispatch", label: "Dispatch center" },
  { id: "completed", label: "Completed" },
  { id: "success", label: "Success %" },
  { id: "avgmin", label: "Avg min" },
  { id: "trend", label: "Trend" },
];

const TREND_LABEL: Record<"up" | "down" | "stable", string> = {
  up: "▲",
  down: "▼",
  stable: "—",
};

const NUMBER_FORMATTER = new Intl.NumberFormat();

function labelize(value: string): string {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
}

function formatMetric(value: number | undefined): string {
  return typeof value === "number"
    ? NUMBER_FORMATTER.format(value)
    : "Unavailable";
}

function formatPercent(value: number | undefined): string {
  return typeof value === "number" ? `${value}%` : "Unavailable";
}

function taskReference(taskNumber: number): string {
  return `TSK-${taskNumber}`;
}

function shipmentReference(shipmentNumber: number | null): string {
  return shipmentNumber === null
    ? "Shipment unavailable"
    : `SHP-${shipmentNumber}`;
}

function MetricCard({ title, value }: { title: string; value: string }) {
  return (
    <Card className="min-w-0">
      <CardContent className="p-4">
        <CardDescription className="truncate text-xs font-medium">
          {title}
        </CardDescription>
        <p className="mt-1 text-2xl font-semibold tracking-[-0.03em] tabular-nums text-zinc-950">
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="outline" size="sm-touch" onClick={onClick}>
      Retry
    </Button>
  );
}

function Breakdown({
  title,
  rows,
}: {
  title: string;
  rows: CarrierAnalyticsCountRow[];
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b px-4 py-3">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bucket</TableHead>
              <TableHead className="w-20 text-right">Count</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={2}
                  className="py-5 text-center text-muted-foreground"
                >
                  No activity recorded.
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>{labelize(row.key)}</TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {formatMetric(row.count)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ScorecardSection() {
  const scorecard = useCarrierScorecard();
  const forbidden = hasHttpStatus(scorecard.error, 403);

  return (
    <section aria-labelledby="scorecard-heading" className="space-y-3">
      <h2 id="scorecard-heading" className="text-sm font-semibold">
        Today
        {scorecard.data
          ? ` ${TREND_LABEL[scorecard.data.trend]} (vs ${formatMetric(
              scorecard.data.completedYesterday
            )} completed yesterday)`
          : ""}
      </h2>
      {scorecard.isLoading && (
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading today's scorecard...
        </p>
      )}
      {scorecard.isFetching && scorecard.data && (
        <p role="status" className="text-xs text-muted-foreground">
          Refreshing today's scorecard...
        </p>
      )}
      {scorecard.error && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 text-sm text-destructive"
        >
          <span>
            {forbidden
              ? "You do not have permission to view today's scorecard."
              : `Today's scorecard is unavailable.${
                  scorecard.data ? " Showing last available values." : ""
                }`}
          </span>
          {!forbidden && <RetryButton onClick={() => scorecard.refetch()} />}
        </div>
      )}
      {!scorecard.isLoading && !scorecard.error && !scorecard.data && (
        <p className="text-sm text-muted-foreground">
          No scorecard data is available.
        </p>
      )}
      {scorecard.data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          <MetricCard
            title="Completed today"
            value={formatMetric(scorecard.data.completedToday)}
          />
          <MetricCard
            title="Failed today"
            value={formatMetric(scorecard.data.failedToday)}
          />
          <MetricCard
            title="Open backlog"
            value={formatMetric(scorecard.data.openTasks)}
          />
          <MetricCard
            title="Received today"
            value={formatMetric(scorecard.data.receivedToday)}
          />
          <MetricCard
            title="Success rate"
            value={formatPercent(scorecard.data.successRate)}
          />
        </div>
      )}
    </section>
  );
}

function AlertsSection() {
  const alerts = useCarrierAlerts();
  const forbidden = hasHttpStatus(alerts.error, 403);
  const data = alerts.data;
  const hasAlerts =
    !!data && (data.failedToday.count > 0 || data.stuckTasks.count > 0);
  const failureBreakdown = data
    ? Object.entries(data.failedToday.breakdown)
    : [];

  return (
    <section aria-labelledby="alerts-heading" className="space-y-3">
      <h2 id="alerts-heading" className="text-sm font-semibold">
        Alerts
      </h2>
      {alerts.isLoading && (
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading alerts...
        </p>
      )}
      {alerts.isFetching && data && (
        <p role="status" className="text-xs text-muted-foreground">
          Refreshing operational alerts...
        </p>
      )}
      {alerts.error && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 text-sm text-destructive"
        >
          <span>
            {forbidden
              ? "You do not have permission to view operational alerts."
              : `Operational alerts are unavailable.${
                  data ? " Showing last available values." : ""
                }`}
          </span>
          {!forbidden && <RetryButton onClick={() => alerts.refetch()} />}
        </div>
      )}
      {!alerts.isLoading && !alerts.error && !data && (
        <p className="text-sm text-muted-foreground">
          No alert data is available.
        </p>
      )}
      {data && !hasAlerts && (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            No active operational alerts.
          </CardContent>
        </Card>
      )}
      {data && hasAlerts && (
        <div className="grid gap-3 lg:grid-cols-2">
          <Card>
            <CardHeader className="px-4 pb-2 pt-4">
              <CardTitle className="text-sm text-red-800">
                Failed today ({formatMetric(data.failedToday.count)})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4">
              {failureBreakdown.length > 0 && (
                <ul className="divide-y text-xs">
                  {failureBreakdown.map(([reason, count]) => (
                    <li
                      key={reason}
                      className="flex justify-between gap-3 py-2"
                    >
                      <span>{labelize(reason)}</span>
                      <span className="tabular-nums">
                        {formatMetric(count)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {data.failedToday.tasks.length > 0 && (
                <ul className="divide-y border-t text-xs">
                  {data.failedToday.tasks.slice(0, 8).map((task) => (
                    <li
                      key={task.id}
                      className="flex justify-between gap-3 py-2"
                    >
                      <span className="font-mono">
                        {taskReference(task.taskNumber)}
                      </span>
                      <span className="text-right text-muted-foreground">
                        {shipmentReference(task.shipmentNumber)} ·{" "}
                        {task.reason ?? task.status ?? "Reason unavailable"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {failureBreakdown.length === 0 &&
                data.failedToday.tasks.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Failure details are unavailable.
                  </p>
                )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="px-4 pb-2 pt-4">
              <CardTitle className="text-sm text-amber-800">
                Aging backlog &gt;4h ({formatMetric(data.stuckTasks.count)})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {data.stuckTasks.tasks.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Backlog details are unavailable.
                </p>
              ) : (
                <ul className="divide-y text-xs">
                  {data.stuckTasks.tasks.slice(0, 8).map((task) => (
                    <li
                      key={task.id}
                      className="flex justify-between gap-3 py-2"
                    >
                      <span className="font-mono">
                        {taskReference(task.taskNumber)}
                      </span>
                      <span className="text-right text-muted-foreground">
                        {shipmentReference(task.shipmentNumber)} ·{" "}
                        {task.status ?? "Status unavailable"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </section>
  );
}

function EfficiencySummary({ row }: { row: CarrierOperatorEfficiencyRow }) {
  return (
    <li className="space-y-2 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.operatorName}</p>
          <p className="truncate text-xs text-muted-foreground">{row.depot}</p>
        </div>
        <span
          role="img"
          aria-label={`Trend ${row.trend}`}
          className="shrink-0 font-semibold"
        >
          {TREND_LABEL[row.trend]}
        </span>
      </div>
      <dl className="grid grid-cols-5 gap-2 text-xs">
        <Metric label="Completed" value={formatMetric(row.tasks.completed)} />
        <Metric label="Success" value={formatPercent(row.successRate)} />
        <Metric
          label="Avg min"
          value={
            row.avgTaskTimeMinutes === null
              ? "Unavailable"
              : formatMetric(row.avgTaskTimeMinutes)
          }
        />
        <Metric label="Failed" value={formatMetric(row.tasks.failed)} />
        <Metric label="Score" value={formatMetric(row.efficiencyScore)} />
      </dl>
    </li>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-[10px] uppercase text-muted-foreground">
        {label}
      </dt>
      <dd className="truncate font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function OperatorEfficiencySection({
  startDate,
  endDate,
  rangeError,
}: {
  startDate: string;
  endDate: string;
  rangeError: string | null;
}) {
  const rangeValid = rangeError === null;
  const efficiency = useCarrierOperatorEfficiency({
    startDate: rangeValid ? startDate : "",
    endDate: rangeValid ? endDate : "",
  });
  const forbidden = hasHttpStatus(efficiency.error, 403);
  const { isVisible, toggle: toggleColumn } = useColumnVisibility(
    "carrier-web:columns:analytics"
  );
  const colClass = (id: string) => (isVisible(id) ? "" : "hidden");

  return (
    <section aria-labelledby="efficiency-heading" className="space-y-3">
      <div>
        <h2 id="efficiency-heading" className="text-sm font-semibold">
          Operator efficiency
        </h2>
        {efficiency.data && (
          <p className="mt-1 text-xs text-muted-foreground">
            Reporting period {formatDateOnly(efficiency.data.period.start)} to{" "}
            {formatDateOnly(efficiency.data.period.end)}
          </p>
        )}
      </div>
      {!rangeValid && (
        <p role="alert" className="text-sm text-destructive">
          {rangeError}
        </p>
      )}
      {rangeValid && efficiency.isLoading && (
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading operator efficiency...
        </p>
      )}
      {rangeValid && efficiency.isFetching && efficiency.data && (
        <p role="status" className="text-xs text-muted-foreground">
          Refreshing operator efficiency...
        </p>
      )}
      {rangeValid && efficiency.error && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 text-sm text-destructive"
        >
          <span>
            {forbidden
              ? "You do not have permission to view operator efficiency."
              : "Operator efficiency is unavailable."}
          </span>
          {!forbidden && <RetryButton onClick={() => efficiency.refetch()} />}
        </div>
      )}
      {rangeValid &&
        !efficiency.isLoading &&
        !efficiency.error &&
        !efficiency.data && (
          <p className="text-sm text-muted-foreground">
            No operator efficiency data is available for this window.
          </p>
        )}
      {efficiency.data && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <MetricCard
              title="Avg tasks / operator"
              value={formatMetric(
                efficiency.data.benchmarks.avgTasksPerOperator
              )}
            />
            <MetricCard
              title="Avg task minutes"
              value={formatMetric(
                efficiency.data.benchmarks.avgTaskTimeMinutes
              )}
            />
            <MetricCard
              title="Avg success rate"
              value={formatPercent(efficiency.data.benchmarks.avgSuccessRate)}
            />
          </div>

          <Card className="overflow-hidden">
            <CardHeader className="flex flex-row items-start justify-between gap-3 border-b px-4 py-3">
              <div>
                <CardTitle className="text-sm">Operator leaderboard</CardTitle>
                <CardDescription className="mt-1 text-xs">
                  Per-operator throughput for the selected window.
                </CardDescription>
              </div>
              <div className="hidden xl:block">
                <ColumnsMenu
                  columns={TOGGLEABLE_COLUMNS}
                  isVisible={isVisible}
                  onToggle={toggleColumn}
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {efficiency.data.operators.length === 0 ? (
                <p className="p-5 text-center text-sm text-muted-foreground">
                  No operator activity in this window.
                </p>
              ) : (
                <>
                  <ul
                    aria-label="Operator efficiency summaries"
                    className="divide-y xl:hidden"
                  >
                    {efficiency.data.operators.map((row) => (
                      <EfficiencySummary key={row.operatorId} row={row} />
                    ))}
                  </ul>
                  <div className="hidden overflow-x-auto xl:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Operator</TableHead>
                          <TableHead className={colClass("dispatch")}>
                            Dispatch center
                          </TableHead>
                          <TableHead
                            className={cn("text-right", colClass("completed"))}
                          >
                            Completed
                          </TableHead>
                          <TableHead
                            className={cn("text-right", colClass("success"))}
                          >
                            Success %
                          </TableHead>
                          <TableHead
                            className={cn("text-right", colClass("avgmin"))}
                          >
                            Avg min
                          </TableHead>
                          <TableHead className="text-right">Score</TableHead>
                          <TableHead
                            className={cn("text-center", colClass("trend"))}
                          >
                            Trend
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {efficiency.data.operators.map((row) => (
                          <TableRow key={row.operatorId}>
                            <TableCell className="font-medium">
                              {row.operatorName}
                            </TableCell>
                            <TableCell className={colClass("dispatch")}>
                              {row.depot}
                            </TableCell>
                            <TableCell
                              className={cn(
                                "text-right tabular-nums",
                                colClass("completed")
                              )}
                            >
                              {formatMetric(row.tasks.completed)}
                            </TableCell>
                            <TableCell
                              className={cn(
                                "text-right tabular-nums",
                                colClass("success")
                              )}
                            >
                              {formatPercent(row.successRate)}
                            </TableCell>
                            <TableCell
                              className={cn(
                                "text-right tabular-nums",
                                colClass("avgmin")
                              )}
                            >
                              {row.avgTaskTimeMinutes === null
                                ? "Unavailable"
                                : formatMetric(row.avgTaskTimeMinutes)}
                            </TableCell>
                            <TableCell className="text-right font-medium tabular-nums">
                              {formatMetric(row.efficiencyScore)}
                            </TableCell>
                            <TableCell
                              className={cn("text-center", colClass("trend"))}
                            >
                              <span
                                role="img"
                                aria-label={`Trend ${row.trend}`}
                              >
                                {TREND_LABEL[row.trend]}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </section>
  );
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { startDate: toLocalISODate(start), endDate: toLocalISODate(end) };
}

function toLocalISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getRangeError(startDate: string, endDate: string): string | null {
  if (!startDate || !endDate) return "Select both a start and end date.";
  if (startDate > endDate) return "Start date must be on or before end date.";
  return null;
}

export default function AnalyticsScreen() {
  const analytics = useCarrierAnalytics();
  const initialRange = defaultDateRange();
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const rangeError = getRangeError(startDate, endDate);
  const data = analytics.data;
  const forbidden = hasHttpStatus(analytics.error, 403);

  return (
    <div className="carrier-page space-y-6">
      <PageHeader
        title="Analytics"
        actions={
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label
                htmlFor="analytics-start-date"
                className="mb-1 block text-[11px] font-medium text-muted-foreground"
              >
                Start
              </label>
              <Input
                id="analytics-start-date"
                aria-label="Analytics start date"
                type="date"
                className="min-w-0 text-xs"
                value={startDate}
                max={endDate}
                aria-invalid={rangeError !== null}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
            <div>
              <label
                htmlFor="analytics-end-date"
                className="mb-1 block text-[11px] font-medium text-muted-foreground"
              >
                End
              </label>
              <Input
                id="analytics-end-date"
                aria-label="Analytics end date"
                type="date"
                className="min-w-0 text-xs"
                value={endDate}
                min={startDate}
                aria-invalid={rangeError !== null}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
          </div>
        }
      />

      <ScorecardSection />
      <AlertsSection />

      <section aria-labelledby="lifetime-heading" className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="lifetime-heading" className="text-sm font-semibold">
            Lifetime totals
          </h2>
          {analytics.dataUpdatedAt > 0 && (
            <p className="text-xs text-muted-foreground">
              Updated {new Date(analytics.dataUpdatedAt).toLocaleTimeString()}
            </p>
          )}
        </div>

        {analytics.isLoading && (
          <p
            role="status"
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading analytics...
          </p>
        )}
        {analytics.isFetching && data && (
          <p role="status" className="text-xs text-muted-foreground">
            Refreshing lifetime analytics...
          </p>
        )}
        {analytics.error && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-3 text-sm text-destructive"
          >
            <span>
              {forbidden
                ? "You do not have permission to view lifetime analytics."
                : "Lifetime analytics are unavailable."}
            </span>
            {!forbidden && <RetryButton onClick={() => analytics.refetch()} />}
          </div>
        )}
        {!analytics.isLoading && !analytics.error && !data && (
          <p className="text-sm text-muted-foreground">
            No lifetime analytics data is available.
          </p>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              <MetricCard
                title="Shipments"
                value={formatMetric(data.totals?.shipments)}
              />
              <MetricCard
                title="Tasks"
                value={formatMetric(data.totals?.tasks)}
              />
              <MetricCard
                title="Trips"
                value={formatMetric(data.totals?.trips)}
              />
              <MetricCard
                title="Pickup bookings"
                value={formatMetric(data.totals?.pickupBookings)}
              />
              <MetricCard
                title="Operators"
                value={formatMetric(data.totals?.operators)}
              />
              <MetricCard
                title="Vehicles"
                value={formatMetric(data.totals?.vehicles)}
              />
              <MetricCard
                title="Dispatch centers"
                value={formatMetric(data.totals?.depots)}
              />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <Breakdown
                title="Shipments by status"
                rows={data.shipmentsByStatus ?? []}
              />
              <Breakdown
                title="Tasks by status"
                rows={data.tasksByStatus ?? []}
              />
              <Breakdown title="Tasks by type" rows={data.tasksByType ?? []} />
              <Breakdown
                title="Trips by status"
                rows={data.tripsByStatus ?? []}
              />
              <Breakdown
                title="Pickup bookings"
                rows={data.pickupBookingsByState ?? []}
              />
              <Breakdown
                title="Operators by status"
                rows={data.operatorsByStatus ?? []}
              />
              <Breakdown
                title="Vehicles by status"
                rows={data.vehiclesByStatus ?? []}
              />
              <Breakdown
                title="Dispatch centers by status"
                rows={data.depotsByStatus ?? []}
              />
            </div>
          </>
        )}
      </section>

      <OperatorEfficiencySection
        startDate={startDate}
        endDate={endDate}
        rangeError={rangeError}
      />
    </div>
  );
}
